import { map, pipe, toPairs } from 'ramda';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox, FilterByButton, MenuTitle, ToggleButtonWithPopover } from '../../../components/common';
import { ContentFilter } from '../../../components/common/WixIcons';
import { MEDIA_TYPE_TRANSLATIONS } from '../../../constants/MediaType';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import {
  ClickOrigins,
  Targets,
} from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { areAllMediaTypesDisabled, stringifiedEnabledMediaTypes } from '../../../utils/mediaType';
import styles from './MediaTypeSelect.scss';
import { IOuter } from './MediaTypeSelect.types';
import { useMediaTypesLabel } from './useMediaTypesLabel';

export const MediaTypeSelect: FC<IOuter> = ({
  mediaTypes,
  onMediaTypeSelect,
  forceWithIcon = true,
  isDisabled = false,
}) => {
  const { trackSearchAndFiltersStarted, trackSearchAndFiltersFilterClick } = useTrackSearchAndFiltersAnalytics();

  const label = useMediaTypesLabel(mediaTypes);

  const handleMediaTypeSelect = useCallback(
    (mediaTypeSelected) => (event) => {
      onMediaTypeSelect(mediaTypeSelected, event.target.checked);

      const updatedMediaTypes = { ...mediaTypes, [mediaTypeSelected]: event.target.checked };

      trackSearchAndFiltersStarted({
        clickOrigin: ClickOrigins.FilterMediaType,
        target: stringifiedEnabledMediaTypes(updatedMediaTypes),
      });
    },
    [onMediaTypeSelect, trackSearchAndFiltersStarted],
  );

  const handleFilterByButtonClick = useCallback(
    (_event, isOpened) => {
      if (isOpened) {
        trackSearchAndFiltersFilterClick({
          target: Targets.MediaType,
        });
      }
    },
    [trackSearchAndFiltersFilterClick],
  );

  const popoverContent = (
    <>
      <MenuTitle text={<FormattedMessage id="media-type-select.title" />} />

      {pipe(
        toPairs,
        map(([mediaType, checked]) => (
          <div key={mediaType} className={styles.checkbox}>
            <Checkbox checked={checked} onChange={handleMediaTypeSelect(mediaType)}>
              <FormattedMessage id={MEDIA_TYPE_TRANSLATIONS[mediaType]} />
            </Checkbox>
          </div>
        )),
      )(mediaTypes)}
    </>
  );

  return forceWithIcon ? (
    <ToggleButtonWithPopover
      selected={!areAllMediaTypesDisabled(mediaTypes)}
      tooltipContent={<span>{label}</span>}
      popoverContent={popoverContent}
      onClick={handleFilterByButtonClick}
      dataHook="media-type-select"
      disabled={isDisabled}
    >
      <ContentFilter />
    </ToggleButtonWithPopover>
  ) : (
    <FilterByButton
      label={<span data-hook="label">{label}</span>}
      menuContent={popoverContent}
      onClick={handleFilterByButtonClick}
      dataHook="media-type-select"
    />
  );
};
